import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCw, Search } from "lucide-react";
import AdminLayout from "./AdminLayout";
import AdminUserLink from "../components/AdminUserLink";
import API from "../services/api";

const CREDIT_TYPES = new Set(["deposit"]);

function AdminPnlStatement() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ credit: 0, debit: 0, net: 0 });
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(getIndiaDateValue());
  const [toDate, setToDate] = useState(getIndiaDateValue());
  const [rangePreset, setRangePreset] = useState("today");
  const [type, setType] = useState("all");

  const loadTransactions = useCallback(async () => {
    try {
      const res = await API.get("/admin/pnl-statement", {
        params: {
          from: fromDate || undefined,
          to: toDate || undefined,
          type,
          search: search.trim() || undefined,
        },
      });
      setTransactions(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setSummary({
        credit: Number(res.data?.summary?.credit || 0),
        debit: Number(res.data?.summary?.debit || 0),
        net: Number(res.data?.summary?.net || 0),
      });
    } catch {
      setTransactions([]);
      setSummary({ credit: 0, debit: 0, net: 0 });
    }
  }, [fromDate, search, toDate, type]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const filtered = useMemo(() => {
    return transactions;
  }, [transactions]);

  const creditTotal = Number(summary.credit || 0);
  const debitTotal = Number(summary.debit || 0);
  const net = Number(summary.net || 0);

  const exportCsv = () => {
    downloadCsv("pnl-statement.csv", [
      ["ID", "Date & Time", "User", "Mobile", "Type", "Amount", "Remark", "Balance"],
      ...filtered.map((item) => [
        item.id,
        formatDateTime(item.created_at),
        item.user_name || `User ${item.user_id}`,
        item.user_phone || "",
        item.type || "",
        Number(item.amount || 0),
        item.remark || "",
        item.balance_after ?? ""
      ])
    ]);
  };

  const applyRangePreset = (preset) => {
    const range = getDateRangeForPreset(preset);
    setRangePreset(preset);
    setFromDate(range.from);
    setToDate(range.to);
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>P&amp;L Statement</h1>
            <p style={subtitle}>Approved deposits and approved withdraws, matched with dashboard totals</p>
          </div>
          <div style={headerActions}>
            <button type="button" style={actionButton} onClick={loadTransactions}><RefreshCw size={18} /> Refresh</button>
            <button type="button" style={iconButton} onClick={exportCsv}><Download size={18} /></button>
          </div>
        </div>

        <div style={statsGrid}>
          <StatBox label="Credit" value={formatMoney(creditTotal)} bg="#19b55b" />
          <StatBox label="Debit" value={formatMoney(debitTotal)} bg="#ef2c46" />
          <StatBox label="Net (Credit - Debit)" value={`${net >= 0 ? "+" : "-"}${formatMoney(Math.abs(net))}`} bg="#f97316" />
        </div>

        <section style={filterCard} className="admin-mobile-two">
          <div style={quickFilterWrap}>
            <span style={quickFilterLabel}>Quick Range</span>
            <div style={quickFilterButtons}>
              <button type="button" style={rangePreset === "today" ? activeQuickButton : quickButton} onClick={() => applyRangePreset("today")}>Today</button>
              <button type="button" style={rangePreset === "yesterday" ? activeQuickButton : quickButton} onClick={() => applyRangePreset("yesterday")}>Yesterday</button>
              <button type="button" style={rangePreset === "week" ? activeQuickButton : quickButton} onClick={() => applyRangePreset("week")}>This Week</button>
              <button type="button" style={rangePreset === "custom" ? activeQuickButton : quickButton} onClick={() => setRangePreset("custom")}>Date Wise</button>
            </div>
          </div>
          <label style={filterLabel}>
            <span>Search</span>
            <div style={searchBox}>
              <Search size={18} color="#94a3b8" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by mobile, remark..." style={searchInput} />
            </div>
          </label>
          <label style={filterLabel}>
            <span>From Date</span>
            <input type="date" value={fromDate} onChange={(e) => { setRangePreset("custom"); setFromDate(e.target.value); }} style={dateInput} />
          </label>
          <label style={filterLabel}>
            <span>To Date</span>
            <input type="date" value={toDate} onChange={(e) => { setRangePreset("custom"); setToDate(e.target.value); }} style={dateInput} />
          </label>
          <label style={filterLabel}>
            <span>Type</span>
            <select value={type} onChange={(e) => setType(e.target.value)} style={typeSelect}>
              <option value="all">All Types</option>
              <option value="deposit">Deposit</option>
              <option value="withdraw">Withdraw</option>
            </select>
          </label>
          <label style={{ ...filterLabel, justifyContent: "end" }}>
            <span>&nbsp;</span>
            <button
              type="button"
              style={clearButton}
              onClick={() => {
                const today = getIndiaDateValue();
                setRangePreset("today");
                setSearch("");
                setType("all");
                setFromDate(today);
                setToDate(today);
              }}
            >
              Today
            </button>
          </label>
        </section>

        <section style={tableCard}>
          <div style={tableHeader}>
            <div>
              <h3 style={tableTitle}>Transactions</h3>
              <span style={tableSub}>Total: {filtered.length} records</span>
            </div>
            <div style={periodChip}>
              <strong>{fromDate && toDate ? `${fromDate} to ${toDate}` : "All time"}</strong>
              <span>{`Credit: ${formatMoney(creditTotal)} | Debit: ${formatMoney(debitTotal)} | Net: ${net >= 0 ? "+" : "-"}${formatMoney(Math.abs(net))}`}</span>
            </div>
          </div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>ID</th>
                <th style={th}>Date &amp; Time</th>
                <th style={th}>User</th>
                <th style={th}>Type</th>
                <th style={th}>Amount</th>
                <th style={th}>Remark</th>
                <th style={th}>Balance</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => {
                const normalizedType = String(item.type || "").toLowerCase();
                const positive = CREDIT_TYPES.has(normalizedType);
                return (
                  <tr key={`${item.type}-${item.id}`}>
                    <td style={td}>{item.id}</td>
                    <td style={td}>{formatDateTime(item.created_at)}</td>
                    <td style={td}>
                      <AdminUserLink userId={item.user_id} name={item.user_name || `User ${item.user_id}`} phone={item.user_phone || "-"} />
                    </td>
                    <td style={td}>
                      <span style={positive ? creditPill : debitPill}>{String(item.type || "-").toUpperCase()}</span>
                    </td>
                    <td style={{ ...td, color: positive ? "#16a34a" : "#dc2626", fontWeight: 800 }}>
                      {positive ? "+" : "-"}{formatMoney(Math.abs(Number(item.amount || 0)))}
                    </td>
                    <td style={td}>{item.remark || "-"}</td>
                    <td style={td}>{item.balance_after != null ? formatMoney(item.balance_after) : "-"}</td>
                  </tr>
                );
              })}
              {!filtered.length && (
                <tr>
                  <td style={emptyCell} colSpan={7}>No transactions found for this range.</td>
                </tr>
              )}
            </tbody>
          </table>
        </section>
      </section>
    </AdminLayout>
  );
}

function StatBox({ label, value, bg }) {
  return (
    <article style={{ ...statBox, background: bg }}>
      <strong style={statValue}>{value}</strong>
      <span style={statLabel}>{label}</span>
    </article>
  );
}

function formatMoney(value) {
  const amount = Number(value || 0);
  if (Math.abs(amount) >= 1000) return `Rs ${(amount / 1000).toFixed(1)}K`;
  return `Rs ${amount.toFixed(0)}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.toLocaleDateString("en-IN", { year: "numeric", month: "2-digit", day: "2-digit" })} ${date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
}

function downloadCsv(filename, rows) {
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const title = { margin: 0, fontSize: "18px", fontWeight: 800 };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "12px" };
const headerActions = { display: "flex", gap: "10px" };
const iconButton = { width: "46px", height: "46px", border: "1px solid #d1d5db", background: "#fff", borderRadius: "0", cursor: "pointer" };
const actionButton = { height: "46px", padding: "0 16px", border: "1px solid #d1d5db", background: "#fff", borderRadius: "12px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", fontWeight: 800, color: "#0f172a" };
const statsGrid = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "16px" };
const statBox = { borderRadius: "20px", padding: "22px", color: "#fff", minHeight: "120px", display: "grid", alignContent: "end" };
const statValue = { fontSize: "22px", fontWeight: 900 };
const statLabel = { marginTop: "8px", fontSize: "13px", opacity: 0.95 };
const filterCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", padding: "16px", display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: "14px" };
const quickFilterWrap = { gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", padding: "0 0 4px" };
const quickFilterLabel = { color: "#334155", fontSize: "12px", fontWeight: 900, textTransform: "uppercase", letterSpacing: ".04em" };
const quickFilterButtons = { display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" };
const quickButton = { minHeight: "38px", border: "1px solid #dbeafe", borderRadius: "999px", background: "#f8fafc", color: "#334155", padding: "0 14px", fontWeight: 900, cursor: "pointer" };
const activeQuickButton = { ...quickButton, background: "linear-gradient(135deg,#2563eb,#1d4ed8)", borderColor: "#2563eb", color: "#fff", boxShadow: "0 8px 18px rgba(37,99,235,.22)" };
const filterLabel = { display: "grid", gap: "8px", color: "#334155", fontSize: "12px", fontWeight: 700 };
const searchBox = { height: "54px", border: "1px solid #cbd5e1", display: "flex", alignItems: "center", gap: "10px", padding: "0 14px", borderRadius: "0" };
const searchInput = { width: "100%", border: 0, outline: 0, fontSize: "16px" };
const dateInput = { height: "54px", border: "1px solid #cbd5e1", borderRadius: "0", padding: "0 14px", fontSize: "16px" };
const typeSelect = { height: "54px", border: "1px solid #cbd5e1", borderRadius: "0", padding: "0 14px", fontSize: "16px" };
const clearButton = { height: "54px", border: "1px solid #bfdbfe", borderRadius: "12px", background: "#eff6ff", color: "#2563eb", fontSize: "15px", fontWeight: 800, cursor: "pointer" };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "20px", overflow: "hidden" };
const tableHeader = { padding: "20px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "20px" };
const tableTitle = { margin: 0, fontSize: "16px", fontWeight: 800 };
const tableSub = { marginTop: "6px", display: "block", color: "#64748b" };
const periodChip = { background: "#eff6ff", border: "1px solid #bfdbfe", color: "#2563eb", borderRadius: "16px", padding: "14px 18px", display: "grid", gap: "6px", minWidth: "360px" };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { textAlign: "left", height: "52px", padding: "0 22px", color: "#64748b", fontSize: "12px", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "18px 22px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", verticalAlign: "top" };
const creditPill = { display: "inline-block", padding: "8px 12px", borderRadius: "10px", background: "#dcfce7", color: "#16a34a", fontWeight: 800, fontSize: "12px" };
const debitPill = { display: "inline-block", padding: "8px 12px", borderRadius: "10px", background: "#fee2e2", color: "#dc2626", fontWeight: 800, fontSize: "12px" };
const emptyCell = { padding: "28px", textAlign: "center", color: "#94a3b8" };

export default AdminPnlStatement;

function getIndiaDateValue() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(new Date());
}

function getIndiaDateParts(date = new Date()) {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  return formatter.format(date);
}

function addDays(dateString, days) {
  const [year, month, day] = String(dateString).split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days, 12, 0, 0));
  return getIndiaDateParts(date);
}

function getDateRangeForPreset(preset) {
  const today = getIndiaDateValue();
  if (preset === "yesterday") {
    const yesterday = addDays(today, -1);
    return { from: yesterday, to: yesterday };
  }

  if (preset === "week") {
    const [year, month, day] = today.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    const dayOfWeek = date.getUTCDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    return { from: addDays(today, mondayOffset), to: today };
  }

  return { from: today, to: today };
}
